import { ColumnOrderState } from "@tanstack/react-table";

import { decodeIndex, encodeIndex, widthFor } from "./columnIndexCodec";

// Encodes a column order as fixed-width letter indices into `defaultOrder`,
// e.g. default `[a,b,c,d]`, current `[c,a,d,b]` → `"cadb"`. `null` when there's
// nothing to persist (empty, unchanged, unknown id, or over 676 columns).
export function encodeColumnOrder(
    currentOrder: ColumnOrderState,
    defaultOrder: ColumnOrderState
): string | null {
    if (currentOrder.length === 0 || defaultOrder.length === 0) {
        return null;
    }
    const width = widthFor(defaultOrder.length);
    if (width === 0) {
        return null;
    }
    if (isIdentityPermutation(currentOrder, defaultOrder)) {
        return null;
    }
    const indexById = new Map<string, number>(
        defaultOrder.map((id, i) => [id, i])
    );
    const indices = currentOrder.map((id) => indexById.get(id));
    if (!indices.every((idx): idx is number => idx !== undefined)) {
        return null;
    }
    return indices.map((idx) => encodeIndex(idx, width)).join("");
}

function isIdentityPermutation(
    currentOrder: ColumnOrderState,
    defaultOrder: ColumnOrderState
): boolean {
    if (currentOrder.length !== defaultOrder.length) {
        return false;
    }
    return currentOrder.every((id, i) => id === defaultOrder[i]);
}

// Decodes the letter indices back into a column order via `defaultOrder`.
// `null` on anything malformed (bad chars, wrong length, out-of-range or
// duplicate indices) so the caller falls back to the default.
export function decodeColumnOrder(
    encoded: string,
    defaultOrder: ColumnOrderState
): ColumnOrderState | null {
    if (encoded.length === 0 || defaultOrder.length === 0) {
        return null;
    }
    const width = widthFor(defaultOrder.length);
    if (width === 0) {
        return null;
    }
    if (encoded.length % width !== 0) {
        return null;
    }
    const indices = Array.from({ length: encoded.length / width }, (_, k) =>
        decodeIndex(encoded.slice(k * width, k * width + width))
    );
    if (
        !indices.every(
            (idx): idx is number =>
                idx !== null && idx >= 0 && idx < defaultOrder.length
        )
    ) {
        return null;
    }
    if (new Set(indices).size !== indices.length) {
        return null;
    }
    return indices.map((idx) => defaultOrder[idx]);
}
